import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAdminAuth } from "../../auth/AdminAuthContext";

const linkBase: React.CSSProperties = {
  display: "block",
  padding: "10px 12px",
  borderRadius: 0,
  textDecoration: "none",
  color: "#222",
  fontWeight: 500,
};

const navWrap: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const sectionTitle: React.CSSProperties = {
  fontSize: 12,
  letterSpacing: 0.6,
  color: "#888",
  textTransform: "uppercase",
  margin: "8px 0 6px 2px",
};

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  supportUnreadCount?: number;
  emailLeadUnreadCount?: number;
  isMobile?: boolean;
  brandName?: string;
  brandLogoUrl?: string;
};

export default function Sidebar({
  isOpen,
  onClose,
  supportUnreadCount = 0,
  emailLeadUnreadCount = 0,
  isMobile = false,
  brandName = "BeyondPendants",
  brandLogoUrl = "",
}: SidebarProps) {
  const { pathname } = useLocation();
  const { canManageContent } = useAdminAuth();

  const activeStyles = (active: boolean): React.CSSProperties => ({
    ...linkBase,
    background: active ? "#0f172a" : "transparent",
    color: active ? "#fff" : "#1e293b",
  });

  return (
    <>
      {isMobile && isOpen && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.44)",
            zIndex: 39,
          }}
        />
      )}

      <aside
        style={{
          width: isMobile ? "min(86vw, 320px)" : 288,
          minWidth: isMobile ? "min(86vw, 320px)" : 288,
          height: "100dvh",
          position: isMobile ? "fixed" : "relative",
          zIndex: 40,
          left: 0,
          top: 0,
          background: "#fff",
          borderRight: "1px solid #e2e8f0",
          transition: "transform 220ms ease",
          transform: isMobile ? (isOpen ? "translateX(0)" : "translateX(-104%)") : "none",
          pointerEvents: isMobile && !isOpen ? "none" : "auto",
          padding: 16,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          boxShadow: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
            justifyContent: "space-between",
          }}
        >
          <Link
            to="/"
            onClick={isMobile ? onClose : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
              textDecoration: "none",
              color: "#0f172a",
            }}
          >
            <div
              style={{
                width: 104,
                height: 44,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {brandLogoUrl ? (
                <img
                  src={brandLogoUrl}
                  alt={brandName}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <span>{brandName.slice(0, 1).toUpperCase()}</span>
              )}
            </div>
          </Link>
          {isMobile ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              style={{
                border: "1px solid #cbd5e1",
                background: "#fff",
                borderRadius: 0,
                padding: "8px 10px",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          ) : null}
        </div>

        <nav style={{ ...navWrap, flex: 1 }}>
          <div style={sectionTitle}>General</div>
          <NavLink
            to="/"
            style={({ isActive }) => activeStyles(isActive || pathname === "/")}
            onClick={isMobile ? onClose : undefined}
            end
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/documentation"
            style={({ isActive }) =>
              activeStyles(isActive || pathname.startsWith("/documentation"))
            }
            onClick={isMobile ? onClose : undefined}
          >
            Admin Documentation
          </NavLink>

          <div style={sectionTitle}>Catalog</div>
          <NavLink
            to="/products"
            style={({ isActive }) =>
              activeStyles(isActive || pathname.startsWith("/products"))
            }
            onClick={isMobile ? onClose : undefined}
          >
            Products
          </NavLink>
          <NavLink
            to="/products/new"
            style={({ isActive }) =>
              activeStyles(isActive || pathname === "/products/new")
            }
            onClick={isMobile ? onClose : undefined}
          >
            New Product
          </NavLink>

          <div style={sectionTitle}>Orders</div>
          <NavLink
            to="/orders"
            style={({ isActive }) =>
              activeStyles(isActive || pathname.startsWith("/orders"))
            }
            onClick={isMobile ? onClose : undefined}
          >
            Orders
          </NavLink>
          <NavLink
            to="/promotions"
            style={({ isActive }) =>
              activeStyles(isActive || pathname.startsWith("/promotions"))
            }
            onClick={isMobile ? onClose : undefined}
          >
            Referrals & Coupons
          </NavLink>

          <NavLink
            to="/reviews"
            style={({ isActive }) =>
              activeStyles(isActive || pathname === "/reviews")
            }
            onClick={isMobile ? onClose : undefined}
            end
          >
            Reviews
          </NavLink>
          <NavLink
            to="/reviews/codes"
            style={({ isActive }) =>
              activeStyles(isActive || pathname.startsWith("/reviews/codes"))
            }
            onClick={isMobile ? onClose : undefined}
          >
            Review Codes
          </NavLink>

          <div style={sectionTitle}>Customers</div>
          <NavLink
            to="/customers/carts"
            style={({ isActive }) =>
              activeStyles(isActive || pathname.startsWith("/customers/carts"))
            }
            onClick={isMobile ? onClose : undefined}
          >
            Active Carts
          </NavLink>
          <NavLink
            to="/customers/email-leads"
            style={({ isActive }) =>
              activeStyles(
                isActive ||
                  pathname.startsWith("/customers/email-leads") ||
                  pathname.startsWith("/email-leads"),
              )
            }
            onClick={isMobile ? onClose : undefined}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span>Email Leads</span>
              {emailLeadUnreadCount > 0 ? (
                <span
                  style={{
                    minWidth: 22,
                    padding: "2px 6px",
                    borderRadius: 0,
                    background: "#16a34a",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  {emailLeadUnreadCount > 99 ? "99+" : emailLeadUnreadCount}
                </span>
              ) : null}
            </span>
          </NavLink>

          <div style={sectionTitle}>Support</div>
          <NavLink
            to="/support"
            style={({ isActive }) =>
              activeStyles(isActive || pathname.startsWith("/support"))
            }
            onClick={isMobile ? onClose : undefined}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span>Chat</span>
              {supportUnreadCount > 0 ? (
                <span
                  style={{
                    minWidth: 22,
                    padding: "2px 6px",
                    borderRadius: 0,
                    background: "#dc2626",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  {supportUnreadCount > 99 ? "99+" : supportUnreadCount}
                </span>
              ) : null}
            </span>
          </NavLink>
          <NavLink
            to="/website-visits"
            style={({ isActive }) =>
              activeStyles(isActive || pathname.startsWith("/website-visits"))
            }
            onClick={isMobile ? onClose : undefined}
          >
            Website Visits
          </NavLink>

          {canManageContent && (
            <>
              <div style={sectionTitle}>SEO</div>
              <NavLink
                to="/seo"
                style={({ isActive }) =>
                  activeStyles(isActive || pathname.startsWith("/seo"))
                }
                onClick={isMobile ? onClose : undefined}
              >
                SEO Dashboard
              </NavLink>

              <div style={sectionTitle}>Content Management</div>
              <NavLink
                to="/content"
                style={({ isActive }) =>
                  activeStyles(isActive || pathname.startsWith("/content"))
                }
                onClick={isMobile ? onClose : undefined}
              >
                Content Studio
              </NavLink>
              <NavLink
                to="/settings/operations"
                style={({ isActive }) =>
                  activeStyles(
                    isActive || pathname.startsWith("/settings/operations"),
                  )
                }
                onClick={isMobile ? onClose : undefined}
              >
                Other Settings
              </NavLink>

              <div style={sectionTitle}>Access</div>
              <NavLink
                to="/admin-users"
                style={({ isActive }) =>
                  activeStyles(isActive || pathname.startsWith("/admin-users"))
                }
                onClick={isMobile ? onClose : undefined}
              >
                Admin Users
              </NavLink>
            </>
          )}
        </nav>

        <div
          style={{
            marginTop: 16,
            fontSize: 12,
            color: "#64748b",
            borderTop: "1px solid rgba(148,163,184,.18)",
            paddingTop: 12,
            paddingBottom: isMobile ? "calc(16px + env(safe-area-inset-bottom))" : 4,
          }}
        >
          <div style={{ fontWeight: 700, color: "#0f172a" }}>Admin Panel</div>
          <div style={{ marginTop: 4 }}>Responsive shell enabled</div>
        </div>
      </aside>
    </>
  );
}
